import { ComponentType, isValidElement, ReactElement, ReactNode } from 'react';

import type { IconProps } from '@nocowanie/icons/common';

interface IconDefinition {
    path?: ReactNode;
    viewBox: string;
}

type NocIcon = ComponentType<IconProps>;

const definitions = new WeakMap<NocIcon, IconDefinition>();

/**
 * Icons in `@nocowanie/icons` are plain functions returning
 * `<NocIconGenerator createIconProps={{ path, viewBox? }} />` (no hooks), so the SVG
 * definition can be read without rendering the Chakra icon machinery.
 */
const getIconDefinition = (icon: NocIcon): IconDefinition => {
    let definition = definitions.get(icon);

    if (!definition) {
        const element = (icon as (props: IconProps) => ReactElement)({});
        const createIconProps = isValidElement(element)
            ? (element.props as { createIconProps?: { path?: ReactNode; viewBox?: string } })
                  .createIconProps
            : undefined;

        definition = {
            path: createIconProps?.path,
            viewBox: createIconProps?.viewBox ?? '0 0 24 24',
        };
        definitions.set(icon, definition);
    }

    return definition;
};

/**
 * Renders a `@nocowanie/icons` icon as a plain SVG (same markup as Chakra's `createIcon`),
 * styled with a static class instead of runtime CSS-in-JS.
 */
export const TileIcon = ({ icon, className }: { icon: NocIcon; className?: string }) => {
    const { path, viewBox } = getIconDefinition(icon);

    return (
        <svg focusable="false" aria-hidden="true" viewBox={viewBox} className={className}>
            {path}
        </svg>
    );
};

/** Icon component of an element returned by helpers such as `getAmenityIcon` */
export const getIconComponent = (element: ReactNode): NocIcon | null =>
    isValidElement(element) && typeof element.type === 'function'
        ? (element.type as NocIcon)
        : null;
